import { createContext, useContext, useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { authApi } from '../services/api'

/**
 * AuthProvider stores the current user (with JWT) in localStorage and exposes
 * login / logout / refreshUser actions. Pattern matches the timesheets app's
 * useAuth so the cognitive load between projects is zero.
 *
 * On mount it checks localStorage for a stored user and pings /api/auth/me to
 * refresh the permission claims (so if an admin granted you canUpload mid-
 * session, you don't have to log out and back in to see the new flag).
 *
 * refreshUser re-fetches /api/auth/me and updates the cached user. Used by
 * the My Account page after a profile or password change so the nav, dashboard,
 * and forms immediately reflect new values without a logout.
 */
const AuthContext = createContext(null)

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        checkAuth()
    }, [])

    async function checkAuth() {
        try {
            // Surface any forced-logout message left behind by the api client.
            const msg = sessionStorage.getItem('ghasc_auth_message')
            if (msg) {
                sessionStorage.removeItem('ghasc_auth_message')
                // Defer slightly so the Toaster mounts first.
                setTimeout(() => toast.error(msg, { duration: 6000 }), 100)
            }

            const stored = localStorage.getItem('ghasc_user')
            if (!stored) return

            const userData = JSON.parse(stored)
            setUser(userData)

            // Refresh from server — picks up any permission changes since login.
            try {
                const response = await authApi.me()
                if (response.success && response.user) {
                    const refreshed = { ...response.user, token: userData.token }
                    setUser(refreshed)
                    localStorage.setItem('ghasc_user', JSON.stringify(refreshed))
                }
            } catch (err) {
                // Token expired or revoked → clear auth, fall through to redirect.
                console.error('Auth refresh failed:', err)
                localStorage.removeItem('ghasc_user')
                setUser(null)
            }
        } catch (err) {
            console.error('Auth check failed:', err)
            localStorage.removeItem('ghasc_user')
            setUser(null)
        } finally {
            setLoading(false)
        }
    }

    async function login(email, password) {
        try {
            const response = await authApi.login(email, password)
            if (response.success) {
                const userData = { ...response.user, token: response.token }
                setUser(userData)
                localStorage.setItem('ghasc_user', JSON.stringify(userData))
                return { success: true }
            }
            return { success: false, message: response.message || 'Login failed' }
        } catch (err) {
            return { success: false, message: 'Network error. Please try again.' }
        }
    }

    async function logout() {
        try {
            await authApi.logout()
        } catch {
            // Best-effort — even if the server call fails, log out locally.
        }
        localStorage.removeItem('ghasc_user')
        setUser(null)
    }

    /**
     * Re-fetch the current user from /api/auth/me and update both state and
     * localStorage. Called after profile updates or password changes so the
     * rest of the app sees fresh values (name, must_change_password, etc.)
     * without requiring a logout.
     */
    async function refreshUser() {
        try {
            const stored = localStorage.getItem('ghasc_user')
            if (!stored) return
            const current = JSON.parse(stored)

            const response = await authApi.me()
            if (response.success && response.user) {
                const refreshed = { ...response.user, token: current.token }
                setUser(refreshed)
                localStorage.setItem('ghasc_user', JSON.stringify(refreshed))
            }
        } catch (err) {
            console.error('refreshUser failed:', err)
        }
    }

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            logout,
            refreshUser,
            isSysAdmin: user?.isSysAdmin === true,
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
    return ctx
}